import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, UserSquare2, Pencil, Trash2, FileSignature, CalendarClock, RefreshCw } from "lucide-react";
import TenantDialog from "@/components/TenantDialog";
import ContractDialog from "@/components/ContractDialog";
import RenewContractDialog from "@/components/RenewContractDialog";
import PaymentCalendar from "@/components/PaymentCalendar";
import { formatMoney } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import { TENANTS } from "@/constants/testIds";
import { toast } from "sonner";

export default function Tenants() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tenantDlg, setTenantDlg] = useState(false);
  const [contractDlg, setContractDlg] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [renewContract, setRenewContract] = useState(null);

  const load = async () => {
    const [t, c, p, pay] = await Promise.all([
      api.get("/tenants"),
      api.get("/contracts"),
      api.get("/properties"),
      api.get("/payments"),
    ]);
    setTenants(t.data);
    setContracts(c.data);
    setProperties(p.data);
    setPayments(pay.data);
    if (!selectedTenantId && t.data.length) setSelectedTenantId(t.data[0].id);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId);
  const selectedContracts = contracts.filter((c) => c.tenant_id === selectedTenantId);
  const selectedPayments = payments.filter((p) => p.tenant_id === selectedTenantId);

  const canEdit = can(user, "manageProperties");

  const delTenant = async (t) => {
    if (!window.confirm(`¿Eliminar inquilino ${t.name}?`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Inquilino eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const delContract = async (c) => {
    const propName = propMap[c.property_id]?.name || "esta propiedad";
    const msg = `¿Eliminar contrato de "${propName}"?\n\nEsto también borrará TODOS los pagos asociados (pendientes y ya cobrados) y la transacción de ingreso que se haya generado.\n\nEsta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Contrato y pagos eliminados");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Inquilinos</h1>
          <div className="text-sm text-muted-foreground mt-1">{tenants.length} inquilinos · {contracts.length} contratos activos.</div>
        </div>
        {canEdit && (
          <Button className="rounded-full" onClick={() => { setEditingTenant(null); setTenantDlg(true); }} data-testid={TENANTS.addBtn}>
            <Plus className="w-4 h-4 mr-1" /> Nuevo inquilino
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 sm:gap-6">
        {/* Tenant list */}
        <div className="space-y-2" data-testid={TENANTS.list}>
          {tenants.map((t) => {
            const active = t.id === selectedTenantId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedTenantId(t.id)}
                className={`w-full text-left rounded-lg border transition-colors p-3 flex items-center gap-3 ${
                  active ? "border-foreground/40 bg-secondary" : "border-border/60 bg-card hover:border-foreground/20"
                }`}
                data-testid={`tenant-item-${t.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-secondary grid place-items-center shrink-0">
                  <UserSquare2 className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{t.email || t.phone || "—"}</div>
                </div>
                {!t.active && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">inactivo</span>}
              </button>
            );
          })}
          {!tenants.length && <div className="text-center py-10 text-muted-foreground text-sm">Sin inquilinos.</div>}
        </div>

        {/* Selected tenant detail */}
        {selectedTenant ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-border/60 bg-card p-5 sm:p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-heading text-2xl font-semibold tracking-tight">{selectedTenant.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {selectedTenant.email || "sin email"} · {selectedTenant.phone || "sin teléfono"}
                  </div>
                  {selectedTenant.document_id && (
                    <div className="text-xs text-muted-foreground mt-1">Doc: {selectedTenant.document_id}</div>
                  )}
                  {selectedTenant.notes && <div className="text-sm mt-3">{selectedTenant.notes}</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingTenant(selectedTenant); setTenantDlg(true); }}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-500 hover:text-rose-500" onClick={() => delTenant(selectedTenant)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Contratos</div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => { setEditingContract(null); setContractDlg(true); }} data-testid={TENANTS.addContractBtn}>
                    <FileSignature className="w-4 h-4 mr-1" /> Nuevo contrato
                  </Button>
                )}
              </div>
              <div className="grid gap-2">
                {selectedContracts.map((c) => (
                  <div key={c.id} className="rounded-lg border border-border/60 bg-card p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{propMap[c.property_id]?.name || c.property_id.slice(0, 8)}</div>
                      <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" />
                        {formatDMY(c.start_date)} → {formatDMY(c.end_date)}
                        {c.adjustment_pct > 0 && ` · ajuste ${c.adjustment_pct}% cada ${c.adjustment_months} meses`}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-lg font-semibold num">{formatMoney(c.monthly_amount, c.currency)}</div>
                      <div className="text-[11px] text-muted-foreground">/ mes</div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingContract(c); setContractDlg(true); }}
                          data-testid={`contract-edit-${c.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-500 hover:text-emerald-500 border-emerald-500/40"
                          onClick={() => setRenewContract(c)}
                          data-testid={`contract-renew-${c.id}`}
                          title="Renovar contrato 1-click"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Renovar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-500 hover:text-rose-500"
                          onClick={() => delContract(c)}
                          data-testid={`contract-delete-${c.id}`}
                          title="Eliminar contrato y todos sus pagos"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                {!selectedContracts.length && <div className="text-center py-6 text-muted-foreground text-sm">Sin contratos.</div>}
              </div>
            </div>

            <PaymentCalendar payments={selectedPayments} onChanged={load} canEdit={canEdit} />
          </div>
        ) : (
          <div className="rounded-xl border border-border/60 bg-card p-10 text-center text-muted-foreground">
            Seleccioná un inquilino para ver su información.
          </div>
        )}
      </div>

      <TenantDialog open={tenantDlg} onOpenChange={setTenantDlg} tenant={editingTenant} onSaved={load} />
      <ContractDialog
        open={contractDlg}
        onOpenChange={(v) => { setContractDlg(v); if (!v) setEditingContract(null); }}
        properties={properties}
        tenants={tenants}
        defaultTenantId={selectedTenantId}
        contract={editingContract}
        onSaved={load}
      />
      <RenewContractDialog
        open={!!renewContract}
        onOpenChange={(v) => !v && setRenewContract(null)}
        contract={renewContract}
        onRenewed={load}
      />
    </div>
  );
}
